import React, { useState } from 'react';
import InlineLayout from '@/shared/layout/inlineLayout';
import Loader from '@/app/routes/Loader';
import ErrorPage403 from '@/app/routes/errorPage403';
import { Snackbar } from '@/shared/components/Snackbar';
import { Tooltip } from '@/shared/components/Tooltip';
import { useUserPrivileges } from '@/shared/hooks/useUserPrivileges';
import { useUserRole } from '@/shared/hooks/useUserRole';
import {
  useViewContactRequest,
  formatContactRequestDate,
} from '../../hooks/useViewContactRequest';
import { useUpdateContactRequestStatus } from '../../hooks/useUpdateContactRequestStatus';
import { useCompleteContactRequest } from '../../hooks/useCompleteContactRequest';
import { useAssignContactRequest } from '../../hooks/useAssignContactRequest';
import type {
  AdminNote,
  ViewContactRequestData,
  ViewContactRequestSource,
} from '../../api/viewContactRequestApiService';
import type { EligibleAssignee } from '../../types/assignContactRequestTypes';
import {
  type ContactRequestStatus,
  TERMINAL_STATUSES,
} from '../../types/contactRequestTypes';
import { CONTACT_REQUEST_LABELS as L } from '../../constants/labels';
import { CONTACT_REQUEST_LABELS as TL } from '../../constants/translations/labels';
import { CONTACT_REQUEST_MESSAGES } from '../../constants/translations/messages';
import CancelContactRequestModal from './CancelContactRequestModal';
import UpdateStatusModal from './UpdateStatusModal';
import CompleteContactRequestModal from './CompleteContactRequestModal';
import AssignContactRequestModal from './AssignContactRequestModal';
import CRHistoryTab from './CRHistoryTab';
import CRResponseTab from './CRResponseTab';
import { InfoField, SectionCard, StatusBadge } from './viewContactRequestUI';

/** Statuses the assign endpoint accepts (`in_review` or already `assigned`). */
const ASSIGNABLE_STATUSES: ReadonlyArray<ContactRequestStatus> = [
  'in_review',
  'assigned',
];

/**
 * Summary tab — client details, subject, message, and address. The section
 * heading differs by source: the my-assigned-task view (RR-1096) names it
 * "Szczegóły zgłoszenia"; the default view keeps "Podstawowe dane".
 */
function BasicInfoTab({
  data,
  source,
}: {
  data: ViewContactRequestData;
  source: ViewContactRequestSource;
}) {
  return (
    <div className="flex flex-col gap-6 ">
      <SectionCard
        title={
          source === 'my-tasks'
            ? L.VIEW_CR_SECTION_DETAILS
            : L.VIEW_CR_SECTION_BASIC_DATA
        }
      >
        <div className="grid grid-cols-2 gap-x-8 gap-y-6">
          <InfoField
            label={L.VIEW_CR_FIELD_CLIENT_ID}
            value={data.client_user_id}
            testId="view-cr-field-client-id"
          />
          <InfoField
            label={L.VIEW_CR_FIELD_CLIENT_NAME}
            value={data.client_name}
            testId="view-cr-field-client-name"
          />
          <InfoField
            label={L.VIEW_CR_FIELD_EMAIL}
            value={data.client_email}
            testId="view-cr-field-email"
          />
          <InfoField label={L.VIEW_CR_FIELD_PHONE} value={data.client_phone} />
          <InfoField
            label={L.VIEW_CR_FIELD_CREATED_AT}
            value={formatContactRequestDate(data.created_at)}
          />
          <InfoField
            label={L.VIEW_CR_FIELD_SUBJECT}
            value={data.subject}
            testId="view-cr-field-subject"
          />
          <InfoField
            label={L.VIEW_CR_FIELD_MESSAGE}
            value={data.message}
            fullWidth
          />
        </div>
      </SectionCard>

      <SectionCard title={L.VIEW_CR_SECTION_ADDRESS}>
        <div className="grid grid-cols-2 gap-x-8 gap-y-6">
          <InfoField
            label={L.VIEW_CR_FIELD_ADDRESS_LINE1}
            value={data.client_address_line1}
          />
          <InfoField
            label={L.VIEW_CR_FIELD_ADDRESS_LINE2}
            value={data.client_address_line2}
          />
          <InfoField label={L.VIEW_CR_FIELD_CITY} value={data.client_city} />
          <InfoField label={L.VIEW_CR_FIELD_STATE} value={data.client_state} />
          <InfoField label={L.VIEW_CR_FIELD_ZIP} value={data.client_zip_code} />
          <InfoField
            label={L.VIEW_CR_FIELD_COUNTRY}
            value={data.client_country}
          />
        </div>
      </SectionCard>
    </div>
  );
}

/** Maps a raw `ADMIN` / `SUPER_ADMIN` role code to its display label. */
function getAssigneeRoleLabel(role: string | null): string | null {
  if (!role) {
    return null;
  }
  const ROLE_VALUE_LABELS: Record<string, string> = {
    ADMIN: TL.ROLE_ADMIN_VALUE.polish,
    SUPER_ADMIN: TL.ROLE_SUPER_ADMIN_VALUE.polish,
  };
  return ROLE_VALUE_LABELS[role] ?? role;
}

/**
 * Strips the leading 2-letter ISO country-code token the backend prefixes
 * onto `assigned_user_phone`, e.g. "PL +48222222222" -> "+48222222222".
 */
function formatAssignedPhone(phone: string | null): string | null {
  if (!phone) {
    return phone;
  }
  const [first, ...rest] = phone.trim().split(' ');
  return first && /^[A-Z]{2}$/.test(first) && rest.length > 0
    ? rest.join(' ')
    : phone;
}

/**
 * "Assignee Details" tab — the assigned admin's data, or an empty-state message.
 * Shows the assignee's id/name/email plus their contact number and role, and an
 * "assigned by" card for the admin who performed the (re)assignment.
 */
function AssigneeDetailsTab({ data }: { data: ViewContactRequestData }) {
  if (!data.assigned_to) {
    return (
      <div
        className="flex flex-1 items-center justify-center border border-[#DCDDE0] rounded-lg p-5"
        data-testid="view-cr-assignee-empty"
      >
        <p className="text-center text-[14px] font-normal leading-5 tracking-[0.25px] text-on-surface-variant">
          {TL.VIEW_CR_ASSIGNEE_EMPTY.polish}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <SectionCard title={TL.ASSIGNEE_DETAILS_LABEL.polish}>
        <div className="grid grid-cols-2 gap-x-8 gap-y-6">
          <InfoField
            label={L.VIEW_CR_FIELD_ASSIGNEE_NAME}
            value={data.assigned_user_name}
            testId="view-cr-field-assignee-name"
          />
          <InfoField
            label={L.VIEW_CR_FIELD_ASSIGNEE_ID}
            value={data.assigned_user_sequential_id}
            testId="view-cr-field-assignee-id"
          />
          <InfoField
            label={L.VIEW_CR_FIELD_ASSIGNEE_ROLE}
            value={getAssigneeRoleLabel(data.assigned_user_role)}
            testId="view-cr-field-assignee-role"
          />
          <InfoField
            label={L.VIEW_CR_FIELD_ASSIGNEE_EMAIL}
            value={data.assigned_user_email}
            testId="view-cr-field-assignee-email"
          />
          <InfoField
            label={L.VIEW_CR_FIELD_ASSIGNEE_PHONE}
            value={formatAssignedPhone(data.assigned_user_phone)}
            testId="view-cr-field-assignee-phone"
          />
        </div>
      </SectionCard>

      <SectionCard
        title={TL.ASSIGNED_BY_LABEL.polish}
        testId="view-cr-assigned-by-section"
      >
        <div className="grid grid-cols-2 gap-x-8 gap-y-6">
          <InfoField
            label={L.VIEW_CR_FIELD_ASSIGNED_BY_NAME}
            value={data.assigned_by_user_name}
            testId="view-cr-field-assigned-by-name"
          />
          <InfoField
            label={L.VIEW_CR_FIELD_ASSIGNED_BY_ROLE}
            value={getAssigneeRoleLabel(data.assigned_by_user_role)}
            testId="view-cr-field-assigned-by-role"
          />
          <InfoField
            label={L.VIEW_CR_FIELD_ASSIGNED_BY_EMAIL}
            value={data.assigned_by_user_email}
            testId="view-cr-field-assigned-by-email"
          />
        </div>
      </SectionCard>
    </div>
  );
}

/** Single timestamped admin note entry rendered inside the notes list. */
function AdminNoteEntry({ note }: { note: AdminNote }) {
  return (
    <div className="flex flex-col gap-2 w-full py-4">
      <p className="text-[14px] font-normal leading-5 tracking-[0.25px] text-on-surface-variant wrap-break-word whitespace-pre-wrap">
        {note.note}
      </p>
      <p className="text-[12px] font-normal leading-4 tracking-[0.4px] text-on-surface-variant">
        {formatContactRequestDate(note.created_at)}
      </p>
    </div>
  );
}

/** "Notatki administratora" tab — lists all admin notes or an empty state. */
function AdminNotesTab({
  adminNotes = [],
}: {
  adminNotes?: AdminNote[] | undefined;
}) {
  return (
    <SectionCard
      title={L.VIEW_CR_SECTION_ADMIN_NOTES}
      testId="view-cr-admin-notes-section"
      className="flex-1"
    >
      {adminNotes.length > 0 ? (
        <div className="flex flex-col">
          {adminNotes.map((n, i) => (
            <AdminNoteEntry key={i} note={n} />
          ))}
        </div>
      ) : (
        <p className="px-5 py-2 text-center text-[14px] font-normal leading-5 tracking-[0.25px] text-on-surface-variant">
          {L.VIEW_CR_NOTES_EMPTY}
        </p>
      )}
    </SectionCard>
  );
}

/** Full-page error screen shown when the contact request cannot be loaded. */
function ViewContactRequestErrorScreen() {
  return (
    <div className="flex items-center justify-center h-full">
      <p className="text-on-surface-variant text-bodyLarge">
        {L.VIEW_CR_ERROR_LOAD}
      </p>
    </div>
  );
}

/**
 * Nav tabs for the standard contact-request view. Includes the completion
 * "Response" tab (RR-1097) so all authorized admins/super-admins can see the
 * assigned admin's note + attachments; it renders an empty state until the
 * request has been completed with a response.
 */
const DEFAULT_NAV_ITEMS = [
  { id: 'podstawowe', label: L.VIEW_CR_NAV_BASIC_INFO },
  { id: 'przydzial', label: TL.ASSIGNEE_DETAILS_LABEL.polish },
  { id: 'response', label: L.VIEW_CR_NAV_RESPONSE },
  { id: 'notatki', label: L.VIEW_CR_NAV_ADMIN_NOTES },
] as const;

/**
 * Nav tabs for the my-assigned-task view (RR-1096): a summary tab, the assignee
 * (+ assigned-by) details, the completion response, and the history timeline.
 * Drops the admin-notes tab.
 */
const MY_TASK_NAV_ITEMS = [
  { id: 'podstawowe', label: L.VIEW_CR_NAV_SUMMARY },
  { id: 'przydzial', label: TL.ASSIGNEE_DETAILS_LABEL.polish },
  { id: 'response', label: L.VIEW_CR_NAV_RESPONSE },
  { id: 'historia', label: L.VIEW_CR_NAV_HISTORY },
] as const;

/**
 * Detail page for a single contact request.
 *
 * Fetches data via `useViewContactRequest`, exposes a status-update flow
 * through `useUpdateContactRequestStatus`, and an assignment flow through
 * `useAssignContactRequest`, showing a success snackbar after each. The
 * "Zmiana statusu" button is hidden once the request reaches a terminal
 * status (completed / cancelled); the "Assign" button only shows while the
 * request is `in_review` or already `assigned` (matching what the assign API
 * accepts) and only on the default view. A 409 conflict (another admin assigned
 * it first) keeps the modal open with an inline error and silently refetches
 * the page in the background.
 *
 * `source` selects which detail endpoint is read (RR-1096): `'my-tasks'` reads
 * the self-scoped assigned-task endpoint and renders the summary / assignee
 * (+ assigned-by) / response / history tab set; `'default'` reads the standard
 * endpoint and keeps the original tabs. Defaults to `'default'`.
 *
 * @param contactRequestId - UUID of the contact request (from the route param)
 * @param source - which detail endpoint / tab set to use
 */
const ViewContactRequest: React.FC<{
  contactRequestId: string;
  source?: ViewContactRequestSource;
}> = ({ contactRequestId, source = 'default' }) => {
  const { data, isLoading, showErrorScreen, accessError, refetch } =
    useViewContactRequest(contactRequestId, source);
  const { privileges, isLoading: isPrivilegesLoading } = useUserPrivileges();
  const { isAdmin, isSuperAdmin, userId } = useUserRole();
  const isMyTask = source === 'my-tasks';

  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>(
    L.VIEW_CR_SUCCESS_STATUS_UPDATED
  );

  const modal = useUpdateContactRequestStatus({
    contactRequestId,
    currentStatus: data?.status as ContactRequestStatus | undefined,
    onSuccess: () => {
      setSuccessMessage(L.VIEW_CR_SUCCESS_STATUS_UPDATED);
      setShowSuccessSnackbar(true);
      refetch();
    },
    onCancelSuccess: () => {
      setSuccessMessage(
        CONTACT_REQUEST_MESSAGES.VIEW_CR_SUCCESS_CANCELLED.polish
      );
      setShowSuccessSnackbar(true);
      refetch();
    },
  });

  const assignModal = useAssignContactRequest({
    contactRequestId,
    onSuccess: (_data, wasReassign) => {
      setSuccessMessage(
        wasReassign
          ? CONTACT_REQUEST_MESSAGES.VIEW_CR_SUCCESS_REASSIGNED.polish
          : CONTACT_REQUEST_MESSAGES.VIEW_CR_SUCCESS_ASSIGNED.polish
      );
      setShowSuccessSnackbar(true);
      refetch();
    },
    onConflict: () => {
      refetch();
    },
  });

  const completeModal = useCompleteContactRequest({
    contactRequestId,
    onSuccess: () => {
      setSuccessMessage(L.COMPLETE_CR_SUCCESS);
      setShowSuccessSnackbar(true);
      refetch();
    },
  });

  if (!isPrivilegesLoading && !privileges.contactRequestView) {
    return <ErrorPage403 />;
  }

  // Only take over the whole screen on the initial load (before any data has
  // arrived). A background refetch — e.g. the silent refresh after a 409
  // assign conflict — keeps the last-loaded page (and any open modal) mounted
  // so the user stays in the assign modal with its inline error instead of the
  // modal flashing away behind a full-screen loader and then reappearing.
  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  if (accessError) {
    return <ErrorPage403 />;
  }

  if (showErrorScreen || !data) {
    return <ViewContactRequestErrorScreen />;
  }

  const isTerminal = TERMINAL_STATUSES.includes(
    data.status as ContactRequestStatus
  );
  // Whether the logged-in admin is the assignee of this request. The my-tasks
  // endpoint is self-scoped so any loaded request is theirs; on the default
  // view we compare the admin's sequential id against the assignee's (RR-1097).
  const isAssignee =
    isMyTask ||
    (!!data.assigned_user_sequential_id &&
      data.assigned_user_sequential_id === userId);
  // "Complete & send response" is available while the request is in progress:
  // to the assigned admin (on both the default and the my-tasks view), and to a
  // SUPER_ADMIN on the default "all contact requests" view regardless of
  // assignment — a super admin has no my-tasks tab, so they complete an
  // in-progress request straight from the all-requests list.
  const showCompleteResponse =
    data.status === 'in_progress' && (isSuperAdmin || (isAdmin && isAssignee));
  // On an in-progress request the only action is "Complete & send response", so
  // the plain "Zmiana statusu" button is hidden for every viewer at that status.
  const showUpdateStatus = !isTerminal && data.status !== 'in_progress';
  // The assign/reassign action is only offered on the default view, and never
  // to the request's own assignee — an admin can't reassign a request that's
  // assigned to themselves away to someone else, whether they reach it from
  // "My Contact Requests" (already excluded by !isMyTask) or from the
  // all-contact-requests list. The my-assigned-task view (RR-1096, per design)
  // exposes only "Zmiana statusu".
  const canAssign =
    !isMyTask &&
    !isAssignee &&
    ASSIGNABLE_STATUSES.includes(data.status as ContactRequestStatus);
  const currentAssignee: EligibleAssignee | null =
    data.assigned_to &&
    data.assigned_user_sequential_id &&
    data.assigned_user_name
      ? {
          id: data.assigned_to,
          user_id: data.assigned_user_sequential_id,
          name: data.assigned_user_name,
        }
      : null;
  const assignButtonLabel = currentAssignee
    ? L.VIEW_CR_BUTTON_REASSIGN
    : TL.VIEW_CR_BUTTON_ASSIGN.polish;
  // Render "Zmiana statusu" as the primary (filled) action only when it shares
  // the header with the Assign/Reassign button — i.e. whenever `canAssign` is
  // true (an `in_review` or `assigned` request the viewer isn't assigned to).
  // With two buttons, updating the status is the emphasised one; when it stands
  // alone (e.g. the assignee's own request) it stays the outlined secondary.
  const updateStatusIsPrimary = canAssign;

  const header = (
    <div className="flex items-center justify-between w-full gap-4 flex-wrap">
      <div className="flex items-center gap-3">
        <p
          className="font-medium text-neutral-10"
          style={{ fontSize: '18px', lineHeight: '28px' }}
          data-testid="view-cr-request-id"
        >
          {data.contact_request_id}
        </p>
        <StatusBadge status={data.status} />
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {canAssign &&
          (privileges.contactRequestAssign ? (
            <button
              type="button"
              onClick={() => assignModal.open(currentAssignee)}
              className="px-4 py-2.5 rounded-lg border border-[#DCDDE0] text-[#474A4C] text-sm font-medium leading-5 tracking-[0.1px] hover:bg-[#F2F4F5] transition-colors cursor-pointer shrink-0"
              data-testid="open-assign-cr-btn"
            >
              {assignButtonLabel}
            </button>
          ) : (
            <Tooltip
              content="Nie masz uprawnień do wykonania tej czynności"
              side="bottom"
              forceShow
              triggerWrapContent
            >
              <button
                type="button"
                disabled
                className="px-4 py-2.5 rounded-lg border border-[#DCDDE0] text-[#474A4C] text-sm font-medium leading-5 tracking-[0.1px] opacity-50 cursor-not-allowed shrink-0"
                data-testid="open-assign-cr-btn"
              >
                {assignButtonLabel}
              </button>
            </Tooltip>
          ))}
        {showUpdateStatus &&
          (privileges.contactRequestUpdateStatus ? (
            <button
              type="button"
              onClick={modal.open}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium leading-5 tracking-[0.1px] transition-colors cursor-pointer shrink-0 ${
                updateStatusIsPrimary
                  ? 'bg-[#2A4455] text-white hover:bg-[#1f3340]'
                  : 'border border-[#DCDDE0] text-[#474A4C] hover:bg-[#F2F4F5]'
              }`}
              data-testid="open-update-cr-status-btn"
            >
              {L.VIEW_CR_BUTTON_UPDATE_STATUS}
            </button>
          ) : (
            <Tooltip
              content="Nie masz uprawnień do wykonania tej czynności"
              side="bottom"
              forceShow
              triggerWrapContent
            >
              <button
                type="button"
                disabled
                className={`px-4 py-2.5 rounded-lg text-sm font-medium leading-5 tracking-[0.1px] opacity-50 cursor-not-allowed shrink-0 ${
                  updateStatusIsPrimary
                    ? 'bg-[#2A4455] text-white'
                    : 'border border-[#DCDDE0] text-[#474A4C]'
                }`}
                data-testid="open-update-cr-status-btn"
              >
                {L.VIEW_CR_BUTTON_UPDATE_STATUS}
              </button>
            </Tooltip>
          ))}
        {showCompleteResponse &&
          (privileges.contactRequestUpdateStatus ? (
            <button
              type="button"
              onClick={completeModal.open}
              className="px-4 py-2.5 rounded-lg bg-[#2A4455] text-white text-sm font-medium leading-5 tracking-[0.1px] hover:bg-[#1f3340] transition-colors cursor-pointer shrink-0 whitespace-nowrap"
              data-testid="open-complete-cr-btn"
            >
              {L.VIEW_CR_BUTTON_COMPLETE}
            </button>
          ) : (
            <Tooltip
              content="Nie masz uprawnień do wykonania tej czynności"
              side="bottom"
              forceShow
              triggerWrapContent
            >
              <button
                type="button"
                disabled
                className="px-4 py-2.5 rounded-lg bg-[#2A4455] text-white text-sm font-medium leading-5 tracking-[0.1px] opacity-50 cursor-not-allowed shrink-0 whitespace-nowrap"
                data-testid="open-complete-cr-btn"
              >
                {L.VIEW_CR_BUTTON_COMPLETE}
              </button>
            </Tooltip>
          ))}
      </div>
    </div>
  );

  const navItems = isMyTask ? MY_TASK_NAV_ITEMS : DEFAULT_NAV_ITEMS;
  const contentByNavId: Record<string, React.ReactNode> = {
    podstawowe: <BasicInfoTab data={data} source={source} />,
    przydzial: <AssigneeDetailsTab data={data} />,
    response: <CRResponseTab response={data.response} />,
    ...(isMyTask
      ? {
          historia: <CRHistoryTab history={data.history} />,
        }
      : {
          notatki: <AdminNotesTab adminNotes={data.admin_notes} />,
        }),
  };

  return (
    <React.Fragment key={contactRequestId}>
      <InlineLayout
        title={L.VIEW_CR_TITLE}
        navItems={[...navItems]}
        contentByNavId={contentByNavId}
        header={header}
        searchParamKey="tab"
        defaultActiveId="podstawowe"
      />

      <UpdateStatusModal
        open={modal.isOpen}
        onClose={modal.close}
        currentStatus={data.status as ContactRequestStatus}
        allowedStatuses={modal.allowedStatuses}
        newStatus={modal.newStatus}
        onNewStatusChange={modal.setNewStatus}
        newStatusError={modal.newStatusError}
        note={modal.note}
        onNoteChange={modal.setNote}
        noteError={modal.noteError}
        isSubmitting={modal.isSubmitting}
        submitError={modal.submitError}
        onSubmit={modal.submit}
        canSubmit={modal.canSubmit}
      />

      <CancelContactRequestModal
        open={modal.isCancelModalOpen}
        onBack={modal.closeCancelModal}
        clientName={data.client_name}
        clientEmail={data.client_email}
        cancelNote={modal.cancelNote}
        onCancelNoteChange={modal.setCancelNote}
        cancelNoteError={modal.cancelNoteError}
        isSubmitting={modal.isSubmitting}
        submitError={modal.cancelSubmitError}
        onSubmit={modal.submitCancellation}
      />

      {showCompleteResponse && (
        <CompleteContactRequestModal
          open={completeModal.isOpen}
          onClose={completeModal.close}
          currentStatus={data.status}
          files={completeModal.files}
          onFilesChange={completeModal.onFilesChange}
          isUploading={completeModal.isUploading}
          toastMessage={completeModal.toastMessage}
          onToast={completeModal.showToast}
          onClearToast={completeModal.clearToast}
          notes={completeModal.notes}
          onNotesChange={completeModal.setNotes}
          notesError={completeModal.notesError}
          isSubmitting={completeModal.isSubmitting}
          submitError={completeModal.submitError}
          onSubmit={completeModal.submit}
        />
      )}

      <AssignContactRequestModal
        open={assignModal.isOpen}
        onClose={assignModal.close}
        assignees={assignModal.assignees}
        isLoadingAssignees={assignModal.isLoadingAssignees}
        isLoadingMoreAssignees={assignModal.isLoadingMoreAssignees}
        hasMoreAssignees={assignModal.hasMoreAssignees}
        onLoadMoreAssignees={assignModal.loadMoreAssignees}
        assigneesError={assignModal.assigneesError}
        assigneeSearch={assignModal.assigneeSearch}
        onAssigneeSearchChange={assignModal.setAssigneeSearch}
        selectedAssigneeId={assignModal.selectedAssigneeId}
        onSelectAssignee={assignModal.setSelectedAssigneeId}
        initialSelectedAssignee={assignModal.initialSelectedAssignee}
        selectedAssigneeError={assignModal.selectedAssigneeError}
        isSubmitting={assignModal.isSubmitting}
        submitError={assignModal.submitError}
        onSubmit={assignModal.submit}
        canSubmit={assignModal.canSubmit}
        isReassign={assignModal.isReassign}
        step={assignModal.step}
        onContinue={assignModal.goToConfirm}
        onBack={assignModal.back}
        notify={assignModal.notify}
        onNotifyChange={assignModal.setNotify}
        clientName={data.client_name ?? ''}
      />

      <Snackbar
        open={showSuccessSnackbar}
        message={successMessage}
        variant="success"
        position="top-center"
        showCloseButton
        onClose={() => setShowSuccessSnackbar(false)}
      />
    </React.Fragment>
  );
};

export default ViewContactRequest;
